// 1) Third-party libraries
import {
    createAction,
    createSelector,
    createSlice,
    PayloadAction,
} from "@reduxjs/toolkit";
import { isEqual, keyBy, merge, uniqWith, values } from "lodash-es";

// 2) Local imports
// 2.1 Constants
import { REPORTS_PAGE } from "../../config/tableAndPageConstants";
import { reportSortByOptions } from "../../constants/reportSortAndFilters";

// 2.3 Project-specific modules � components/types
import { PageData } from "../common/types";
import {
    GetReportsRequestPayload,
    ReportHistory,
    ReportState,
} from "./types";

// 2.4 Utility functions
import { ResponseModalObject } from "../../utils/commonInterfaces";

// 2.5 Redux actions / store
import type { RootState } from "../storeTypes";

const actionPrefix = 'report';

// Type-safe initialState!
export const initialState: ReportState = {
    loading: false,
    errorMessages: [],
    data: [],
    pageData: {
        pageSize: REPORTS_PAGE.pageSize,
        currentPage: 0,
        hasNextPage: false,
    },
    filters: {
    },
    tableFilter: undefined,
    sortBy: reportSortByOptions[0].value,
    sortAscending: true,
    activeData: {
        record: {},
        loading: false,
        selectedId: null,
        errorMessages: [],
    }
};

const reportSlice = createSlice({
    name: actionPrefix,
    initialState,
    reducers: {
        getReportsForOrganisationRequestAction: (
            state,
            _action: PayloadAction<GetReportsRequestPayload>
        ) => {
            state.loading = true;
        },
        getReportsRequestAction: (
            state,
            _action: PayloadAction<GetReportsRequestPayload>
        ) => {
            state.loading = true;
        },
        getReportsSuccessAction: (
            state,
            action: PayloadAction<{
                data: ReportHistory[];
                pageData: PageData;
                mergeData?: boolean;
            }>
        ) => {
            if (action.payload.mergeData === true) {
                state.data = values(
                    merge(
                        keyBy(state.data, 'Id'),
                        keyBy(action.payload.data, 'Id')
                    )
                );
            } else if (action.payload.pageData.currentPage === 0) {
                state.data = action.payload.data;
            } else {
                state.data = uniqWith(
                    [...state.data, ...action.payload.data],
                    isEqual
                );
            }

            state.loading = false;
            state.pageData = action.payload.pageData;
            state.errorMessages = initialState.errorMessages;
        },
        getReportsErrorAction: (
            state,
            action: PayloadAction<string[]>
        ) => {
            state.loading = false;
            state.data = initialState.data;
            state.errorMessages = action.payload;
        },
        updateReportsTableFilterAction: (
            state,
            action: PayloadAction<string>
        ) => {
            state.tableFilter = action.payload;
        },
        updateReportsSortByAndStateAction: (
            state,
            action: PayloadAction<{
                sortBy: GetReportsRequestPayload['sortBy'];
                sortAscending: boolean;
            }>
        ) => {
            state.sortBy = action.payload.sortBy;
            state.sortAscending = action.payload.sortAscending;
        },
        updateReportsFiltersAction: (
            state,
            action: PayloadAction<GetReportsRequestPayload['filters']>
        ) => {
            state.filters = action.payload;
        },
        setSelectedReportIdRequestAction: (
            state,
            action: PayloadAction<string | null>
        ) => {
            state.activeData.selectedId = action.payload;
        },
        getReportDataForOrganisationRequestAction: {
            reducer: (
                state,
                _action: PayloadAction<{
                    ReportJobId: string;
                    Region: string;
                }>
            ) => {
                state.activeData.loading = true;
            },
            prepare: (
                ReportJobId: string,
                Region: string
            ) => ({
                payload: {
                    ReportJobId,
                    Region
                }
            })
        },
        getReportDataRequestAction: (
            state,
            _action: PayloadAction<string>
        ) => {
            state.activeData.loading = true;
        },
        getReportDataSuccessAction: (
            state,
            action: PayloadAction<{ record: {} }>
        ) => {
            state.activeData.record = action.payload.record;
            state.activeData.loading = false;
            state.activeData.errorMessages = initialState.activeData.errorMessages;
        },
        getReportDataErrorAction: (
            state,
            action: PayloadAction<string[]>
        ) => {
            state.activeData.loading = false;
            state.activeData.record = initialState.activeData.record;
            state.activeData.errorMessages = action.payload;
        },
        clearReportDataAction: (state) => {
            state.activeData = initialState.activeData;
        },
        clearReportFiltersAction: (state) => {
            state.filters = initialState.filters;
        },
    }
});

const { actions, reducer } = reportSlice;

// Actions
export const {
    getReportsForOrganisationRequestAction,
    getReportsRequestAction,
    getReportsSuccessAction,
    getReportsErrorAction,
    updateReportsTableFilterAction,
    updateReportsSortByAndStateAction,
    updateReportsFiltersAction,
    setSelectedReportIdRequestAction,
    getReportDataForOrganisationRequestAction,
    getReportDataRequestAction,
    getReportDataSuccessAction,
    getReportDataErrorAction,
    clearReportDataAction,
    clearReportFiltersAction
} = actions;

export const downloadCompleteReportAction = createAction(
    `${actionPrefix}/downloadCompleteReportAction`,
    function prepare(
        payload: any,
        callback?: (response: ResponseModalObject) => void
    ) {
        return {
            payload: {
                payload,
                callback
            }
        };
    }
);

// Selectors
export const getReportState = (state: RootState) => state.report;
export const getReportData = createSelector(
    getReportState,
    (report) => report.activeData
);
export const getSelectedReportId = createSelector(
    getReportData,
    (activeData) => activeData.selectedId
);

// Reducer
export default reducer;
